import { useGTMDispatch } from '@elgorditosalsero/react-gtm-hook'
import MessageModal from 'components/Modals/MessageModal'
import { RiseUpErrors } from 'constants/errors'
import useErrorMessage from 'hooks/useErrorMessage'
import { useGTMData } from 'hooks/useGTMData'
import { useUTMTracking } from 'hooks/useUTMTracking'
import queryString from 'query-string'
import React, { useEffect, useState } from 'react'
import { AiFillCheckCircle, AiFillCloseCircle } from 'react-icons/ai'
import { useLocation, useNavigate } from 'react-router-dom'
import { getModifiedUrlWithStep } from 'utils'
import CompanyLogo from '../../assets/afbc.png'
import Side6 from '../../assets/new_side_6.jpg'
import Button from '../../components/Button'
import LoaderModal from '../../components/Modals/LoaderModal'
import Stepper from '../../components/Stepper'
import { useRiseUpAPIV1 } from '../../hooks/useRiseUpAPIV1'
import { IFormProps, InvestingPersonEnum } from '../../interfaces'

const SelfAccreditingInvestor = (props: IFormProps) => {
  const [isLoading, setIsLoading] = useState(false)
  const [idologyError, setIdologyError] = useState(false)
  const sendDataToGTM = useGTMDispatch()
  const { errorMessage, setErrorMessage } = useErrorMessage()
  const gtmData = useGTMData(props.getValues)
  const {
    getFormattedUTMParams, // Helper to generate a complete GTM event object
  } = useUTMTracking()
  const navigate = useNavigate()
  const location = useLocation()

  const handleAccreditedInvestor = (value: string) => {
    props.setValue('investingProfile.accreditedInvestor', value)
  }

  const { updateOnboardProgress, performIdologyKyc, getOnboardProgress } = useRiseUpAPIV1()

  const getFreshBearerToken = async () => {
    const getOnboardProgressRes = await getOnboardProgress(props.getValues('onboardingId'))
    if (getOnboardProgressRes.status === 200) {
      return getOnboardProgressRes.data.accessToken
    }
  }

  const handleNextStep = () => {
    const pathname = location.pathname
    const currentParams = queryString.parse(location.search)
    const updatedUrl = getModifiedUrlWithStep(
      pathname,
      currentParams,
      props.getValues('investingProfile.accreditedInvestor') === 'yes' ? 'acknowledgement' : 'unaccredited',
    )

    if (
      props.getValues('investingProfile.option') === InvestingPersonEnum.RETIREMENT &&
      props.getValues('investingProfile.retirementAccountType') === 'TRUST_ACCOUNT'
    ) {
      navigate(updatedUrl)
      props.nextStep(props.getValues('investingProfile.accreditedInvestor') === 'yes' ? '7' : '6B-1')
    } else {
      // NOTE: page 6A-1 is an optional page, default next is to page 7 or 6B-1
      navigate(updatedUrl)
      props.nextStep(props.getValues('investingProfile.accreditedInvestor') === 'yes' ? '7' : '6B-1')
    }
  }

  const handleSubmit = async () => {
    let accessToken = ''

    setIsLoading(true)
    const data = {
      ...props.getValues(),
      // NOTE: page 6A-1 is an optional page, default next is to page 7 or 6B-1
      lastCompletedStage: props.getValues('investingProfile.accreditedInvestor') === 'yes' ? '7' : '6B-1',
    }

    try {
      accessToken = await getFreshBearerToken()

      const updateOnboardProgressRes = await updateOnboardProgress(data)
      const formattedUTMParams = getFormattedUTMParams()

      if (updateOnboardProgressRes.status === 201 || updateOnboardProgressRes.status === 200) {
        sendDataToGTM({
          event: 'funnel_step',
          step_title: 'ACCREDITED_INVESTOR_TYPE',
          ...gtmData,
          ...formattedUTMParams,
        })
        // NOTE: Disable in the meantime
        // await performIdologyKyc(accessToken)

        handleNextStep()
        // if (idologyRes.data.response['summary-result'].key === 'id.failure') {
        //   setIdologyError(true)
        // } else {
        //   handleNextStep()
        // }
      }
    } catch (error: any) {
      // catch the duplicate errors
      if (error.response.data === RiseUpErrors.DuplicatePhoneNumber) {
        setErrorMessage(RiseUpErrors.DuplicatePhoneNumber + ' Please update the phone number.')
        return
      }
      if (error.response.data === RiseUpErrors.DuplicateSSN) {
        setErrorMessage(RiseUpErrors.DuplicateSSN + ' Please update the ssn.')
        return
      }
      handleNextStep()
      setIsLoading(false)
    }
  }

  useEffect(() => {
    props.setCurrentStage(2)
  }, []) // eslint-disable-line

  // useEffect(() => {
  //   sendDataToGTM({
  //     event: 'funnel_step',
  //     step_title: 'ACCREDITED_INVESTOR_TYPE',
  //     ...gtmData,
  //   })
  // }, [])

  return (
    <div className="flex h-screen w-full flex-col md:flex-row md:justify-start">
      <div className="mt-10 flex flex-col sm:w-screen md:mt-0 md:min-h-screen md:w-[45%] lg:w-[40%] xl:w-[35%]">
        <div className="relative w-screen px-10 sm:h-auto sm:px-16 md:hidden">
          <img className="w-screen" src={CompanyLogo} alt="CompanyLogo" />
        </div>
        <div className="relative hidden flex-grow md:flex">
          <img className="flex h-screen w-full" src={Side6} alt="Side6" />
        </div>
      </div>

      <div className="flex flex-col p-10 sm:w-[100%] md:w-[55%] lg:h-screen lg:w-[60%] lg:overflow-y-scroll xl:w-[65%]">
        {/* <div className="justify-center mb-10 sm:hidden lg:flex">
          <div className="w-1/2">
            <img className="w-screen" src={CompanyLogo} alt="CompanyLogo" />
          </div>
        </div> */}

        <div className="md:my-3">
          <Stepper current={props.currentStage} />
        </div>

        <div className="py-4 text-left text-2xl font-semibold text-gray-900 md:py-12 md:pb-10 md:text-4xl">
          Invest in Armed Forces Brewing
        </div>

        <div className="lg:text-1xl pb-8 text-left text-lg font-medium text-gray-900 lg:pb-8">
          {props.getValues('investingProfile.option') === InvestingPersonEnum.IN_BEHALF_OF_AN_ENTITY
            ? 'Is the company an accredited investor?'
            : props.getValues('investingProfile.option') === InvestingPersonEnum.RETIREMENT &&
                props.getValues('investingProfile.retirementAccountType') === 'TRUST_ACCOUNT'
              ? 'Is the trust an accredited investor?'
              : props.getValues('investingProfile.option') === InvestingPersonEnum.JOINTLY
                ? 'Are you both accredited investors?'
                : 'Are you an accredited investor?'}
        </div>

        <div className="flex justify-center lg:justify-start">
          <div className="flex w-full justify-center gap-4">
            <div
              className={`flex h-[64px] w-[50%] items-center gap-3 rounded-md border-2 px-6 py-4 text-center hover:cursor-pointer ${
                props.watch('investingProfile.accreditedInvestor') === 'yes' ? 'border-indigo-500 shadow-xl' : ''
              }`}
              onClick={() => {
                handleAccreditedInvestor('yes')
              }}
            >
              <AiFillCheckCircle size={'32px'} className="text-base text-green-600" />
              <div className="text-xl md:text-2xl">Yes</div>
            </div>
            <div
              className={`flex h-[64px] w-[50%] items-center gap-3 rounded-md border-2 px-6 py-4 text-center hover:cursor-pointer ${
                props.watch('investingProfile.accreditedInvestor') === 'no' ? 'border-indigo-500 shadow-xl' : ''
              }`}
              onClick={() => {
                handleAccreditedInvestor('no')
              }}
            >
              <AiFillCloseCircle size={'32px'} className="text-base text-red-500" />
              <div className="text-xl md:text-2xl">No</div>
            </div>
          </div>
        </div>

        <div className="mt-4 text-base leading-5 text-gray-500">
          {props.getValues('investingProfile.option') === 'IN_BEHALF_OF_AN_ENTITY' ? (
            'A company is an accredited investor if (a) all of the equity owners are accredited investors, (b) it is a corporation or limited liability company not formed for the specific purpose of acquiring the securities offered, with total assets in excess of $5,000,000 or (c) otherwise qualifies as an accredited investor under 17 CFR §230.501.'
          ) : props.getValues('investingProfile.option') === InvestingPersonEnum.RETIREMENT &&
            props.getValues('investingProfile.retirementAccountType') === 'TRUST_ACCOUNT' ? (
            'A trust is an accredited investor if (1) the trust has total assets in excess of $5,000,000 and was not formed for the specific purpose of acquiring the securities offered and if the investment is being directed by a “sophisticated person” as described in applicable federal law or (2) the trust otherwise meets the criteria of 17 CFR § 230.501.  '
          ) : (
            <>
              You are an "accredited" investor if your net worth, individually or jointly with your spouse or spousal
              equivalent is more than $1,000,000 not including your primary residence or if you earned more than
              $200,000, or more than $300,000 with your spouse or spousal equivalent, in the past two calendar years and
              you reasonably expect to reach the same income level in the current calendar year or if you meet one of
              the criteria listed{' '}
              <span
                className="cursor-pointer underline"
                onClick={() => {
                  window.open('https://www.sec.gov/education/capitalraising/building-blocks/accredited-investor')
                }}
              >
                here.
              </span>
            </>
          )}
        </div>

        <div className="w-full justify-center gap-3 py-10 sm:block lg:flex">
          <div className="flex flex-col sm:w-[100%] lg:w-[20%]">
            <Button btnName={'Back'} onClickHandler={() => props.previousStep('5')} />
          </div>
          <div className="Flex flex-col sm:w-[100%] lg:w-[80%]">
            <Button
              btnName={'Next: Subscription Agreement'}
              btnType={'button'}
              onClickHandler={handleSubmit}
              disabled={!props.getValues('investingProfile.accreditedInvestor')}
            />
          </div>
        </div>
      </div>
      <LoaderModal isVisible={isLoading} />
      <MessageModal
        isVisible={idologyError || errorMessage !== ''}
        onProceed={() => {
          // automatically set the step to appropriate form duplicate error
          if (errorMessage === RiseUpErrors.DuplicatePhoneNumber + ' Please update the phone number.') {
            setErrorMessage('')
            props.nextStep('3')
            return
          }
          if (errorMessage === RiseUpErrors.DuplicateSSN + ' Please update the ssn.') {
            setErrorMessage('')
            props.nextStep('4')
            return
          }
          setErrorMessage('')
          setIdologyError(false)
          handleNextStep()
        }}
        onCancel={() => {
          setErrorMessage('')
          setIdologyError(false)
        }}
        title={'Error'}
        description={errorMessage !== '' ? errorMessage : 'An error has occured.'}
        type={'error'}
        btn2={'Ok'}
      />
    </div>
  )
}

export default SelfAccreditingInvestor
